import {
  AutomationRequest,
  AutomationType,
  AutomationTypesLabels,
  DateParams
} from 'app/console-api/automations/types';
import { BodyText, FlexColumn, FlexRow, SvgIcon } from '@make-software/cspr-design';
import React, { useMemo } from 'react';
import {
  AutomationControl,
  automationItemSchema
} from 'app/components/management/common/autoamtions/automation-control';
import styled from 'styled-components';
import DeleteBinIcon from 'assets/icons/ic-delete-bin.svg';
import {
  ErrorText,
  ErrorTextContainer,
  StyledDismissButton
} from 'app/components/common/common-styles';
import { array } from 'yup';
import { parseDate } from 'app/components/utils/date-time';
import { getDefaultParamsByType } from 'app/components/common/automation/registry';
import { AutomationOption } from 'app/components/common/automation/utils';

const StyledEmptyListBlock = styled(FlexRow)(({ theme }) =>
  theme.withMedia({
    width: '100%',
    minHeight: '92px',
    borderRadius: '12px',
    marginTop: '8px',
    marginBottom: '8px',
    backgroundColor: theme.styleguideColors.fillSecondary,
    alignItems: 'center',
    justifyContent: 'center'
  })
);

const StyledAutomationControlWrap = styled(FlexRow)(({ theme }) =>
  theme.withMedia({
    gap: '4px',
    width: '100%',
    marginTop: '8px',
    marginBottom: '8px',
    alignItems: 'center',
    justifyContent: 'space-between',
    border: '1px solid',
    padding: '8px',
    borderColor: theme.styleguideColors.borderPrimary,
    borderRadius: '12px'
  })
);

const StyledAddControlButton = styled(BodyText)(({ theme }) =>
  theme.withMedia({
    cursor: 'pointer',
    fontWeight: '400',
    lineHeight: '24px',
    marginBottom: '48px',
    '&:hover': {
      color: theme.styleguideColors.fillPrimaryBlueClick
    }
  })
);

export const automationBlockValidation = array()
  .of(automationItemSchema)
  // no more than 3, as in your UI
  .max(3, 'You can define up to 3 automation rules')
  // only one date-start and one date-end
  .test('unique-date-end-greater-start', 'Date end should be greater than Date start.', (autos) => {
    if (!Array.isArray(autos)) return true;
    const dateStartParams = autos.find((a) => a.type === AutomationType.DateStart)?.params;
    const dateEndParams = autos.find((a) => a.type === AutomationType.DateEnd)?.params;
    if (dateStartParams && dateEndParams) {
      const dateEnd = parseDate((dateEndParams as DateParams).at);
      const dateStart = parseDate((dateStartParams as DateParams).at);
      return dateEnd! > dateStart!;
    }
    return true;
  })
  .test(
    'unique-types',
    'Automation types must be unique. You have duplicate automation types.',
    (autos) => {
      if (!Array.isArray(autos)) return true;
      const types = autos.map((a) => a.type);
      return new Set(types).size === types.length;
    }
  )
  // when traffic-target: params.target must be > 0, we already check in per-item, this is cross-rule stuff
  .nullable();

type AutomationsControlBlockProps = {
  automations: AutomationRequest[];
  onChange: (automations: AutomationRequest[]) => void;
  errors?: any;
} & Record<string, any>;

export const AutomationsControlsBlock = ({
  automations,
  onChange,
  errors
}: AutomationsControlBlockProps) => {
  const options = useMemo<AutomationOption[]>(
    () =>
      Object.entries(AutomationTypesLabels).map(([value, label]) => ({
        value: value as AutomationType,
        label
      })),
    []
  );

  const updateOne = (index: number, next: AutomationRequest) => {
    const copy = [...automations];
    copy[index] = next;
    onChange(copy);
  };

  const addOne = () => {
    if (automations.length === 3) return;
    onChange([
      ...automations,
      {
        id: -1,
        type: options[0].value,
        // params defaults by type
        params: getDefaultParamsByType(options[0].value)
      }
    ]);
  };

  const removeOne = (index: number) => {
    onChange(automations.filter((_, idx) => idx !== index));
  };

  return (
    <FlexColumn style={{ width: '100%' }}>
      <BodyText size={1} scale="sm">
        Automations rules
      </BodyText>

      {automations.length ? (
        automations.map((automation, idx) => (
          <StyledAutomationControlWrap key={idx}>
            <FlexColumn style={{ flex: 4 }}>
              <AutomationControl
                index={idx}
                automation={automation}
                options={options}
                onChange={(next) => updateOne(idx, next)}
                errors={Array.isArray(errors) ? errors[idx]?.params : null}
              />
            </FlexColumn>
            <FlexColumn style={{ flex: 1, width: '36px', alignItems: 'center' }}>
              <StyledDismissButton
                color={'utility'}
                height={'36'}
                onClick={() => removeOne(idx)}
                style={{ width: '36px' }}
              >
                <SvgIcon src={DeleteBinIcon} size={16} />
              </StyledDismissButton>
            </FlexColumn>
          </StyledAutomationControlWrap>
        ))
      ) : (
        <StyledEmptyListBlock>No automations yet</StyledEmptyListBlock>
      )}

      <FlexRow>
        <StyledAddControlButton
          size={3}
          scale={'sm'}
          variation={automations.length === 3 ? 'gray' : 'blue'}
          onClick={addOne}
        >
          + Add rule
        </StyledAddControlButton>
      </FlexRow>
      {errors && (
        <ErrorTextContainer justify={'flex-start'} top={'46px'}>
          <ErrorText size={3}>{errors?.message}</ErrorText>
        </ErrorTextContainer>
      )}
    </FlexColumn>
  );
};
